import { useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '../lib/stripe.js';
import { confirmPurchase } from '../api/client.js';
import { ModalOverlay } from '../components/ModalOverlay.js';

function PurchaseForm({
  saveCard,
  onSaveCardChange,
  onPurchased,
  onClose,
}: {
  saveCard: boolean;
  onSaveCardChange: (v: boolean) => void;
  onPurchased: (sessionMinutes: number, added: number) => void;
  onClose: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!stripe || !elements) return;
    setBusy(true);
    setErr(null);
    const { error, paymentIntent } = await stripe.confirmPayment({ elements, redirect: 'if_required' });
    if (error) {
      setErr(error.message ?? 'Could not complete purchase.');
      setBusy(false);
      return;
    }
    try {
      const res = await confirmPurchase(paymentIntent!.id, saveCard);
      onPurchased(res.sessionMinutes, res.added);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <PaymentElement options={{ wallets: { link: 'never' } }} />
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 shrink-0 accent-brand"
          checked={saveCard}
          onChange={(e) => onSaveCardChange(e.target.checked)}
        />
        <span className="text-sm font-medium text-ink-800 dark:text-ink-100">
          Save this card for future purchases.
        </span>
      </label>
      {err ? <div className="notice-error">{err}</div> : null}
      <div className="flex gap-2 justify-end">
        <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary" onClick={() => { void submit(); }} disabled={busy || !stripe}>
          {busy ? 'Purchasing…' : 'Purchase'}
        </button>
      </div>
    </div>
  );
}

/**
 * Embedded Payment Element modal for a package purchase when the learner has
 * no card on file. `clientSecret` comes from a freshly-created PaymentIntent
 * (see usePackagePurchase's `buy`). No PAN ever touches our code —
 * PaymentElement renders Stripe's iframe, and we only ever pass the resulting
 * paymentIntentId to the backend.
 *
 * The "Save this card" checkbox decides card persistence at confirm time: its
 * value is sent as `saveCard` to `confirmPurchase` after the PaymentIntent
 * succeeds, so the backend saves (true), detaches (false), or — for other
 * callers of `confirmPurchase` that omit the flag — leaves an existing saved
 * card untouched.
 */
export function PurchaseModal({
  clientSecret,
  saveCard: initialSaveCard = true,
  onPurchased,
  onClose,
}: {
  clientSecret: string;
  saveCard?: boolean;
  onPurchased: (sessionMinutes: number, added: number) => void;
  onClose: () => void;
}) {
  const [saveCard, setSaveCard] = useState(initialSaveCard);
  return (
    <ModalOverlay
      role="dialog"
      aria-modal="true"
      aria-label="Complete purchase"
      className="z-50 bg-ink-900/60"
    >
      <div className="max-w-sm w-[90%] mx-auto rounded-2xl bg-canvas-light dark:bg-canvas-dark p-6 shadow-xl ring-1 ring-ink-900/10 dark:ring-white/10">
        <h2 className="h-card text-ink-900 dark:text-ink-100 mb-4">Complete purchase</h2>
        <Elements stripe={getStripe()} options={{ clientSecret }}>
          <PurchaseForm saveCard={saveCard} onSaveCardChange={setSaveCard} onPurchased={onPurchased} onClose={onClose} />
        </Elements>
      </div>
    </ModalOverlay>
  );
}
